/** Widgetlar uchun umumiy kichik bo'laklar: halqa (Apple Activity kabi), ro'yxat qatori, katta raqam, bo'sh holat. */
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Progress halqasi: 0..1; rang -- `currentColor` (mono: `text-ink`, bajarilganda `text-success`), iz -- `--color-fill` */
export function Ring({ value, size = 64, stroke = 7, className, children }: { value: number; size?: number; stroke?: number; className?: string; children?: ReactNode }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));
  return (
    <div className={cn("relative shrink-0", className)} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-fill)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - v)}
          className="transition-[stroke-dashoffset] duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
    </div>
  );
}

/** Ro'yxat qatori: ixtiyoriy nuqta, sarlavha, ostida izoh, o'ngda qisqa matn; bosilsa ochiladi */
export function WidgetRow({ title, sub, right, dot, onClick, muted }: { title: string; sub?: string; right?: ReactNode; dot?: string; onClick?: () => void; muted?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn("flex w-full items-center gap-2 rounded-[8px] px-1.5 py-[3px] text-left transition-colors", onClick ? "hover:bg-fill" : "cursor-default")}
    >
      {dot && <span className={cn("h-2 w-2 shrink-0 rounded-full", dot)} aria-hidden />}
      <span className="min-w-0 flex-1 leading-tight">
        <span className={cn("block truncate text-[12px] font-medium", muted && "text-muted")}>{title}</span>
        {sub && <span className="block truncate text-[11px] text-muted">{sub}</span>}
      </span>
      {right !== undefined && <span className="shrink-0 text-[11px] text-muted tabular-nums">{right}</span>}
    </button>
  );
}

/**
 * Widget raqamlari shkalasi (2026-10-18): bosh raqam 32px (`BigNumber`, kurs), kalendar kuni 36px, halqa ichidagi raqam 20px,
 * ro'yxat sarlavhasidagi son 22px; yorliqlar 10-11px. Hammasi `leading-none`, `tabular-nums`, tracking -0.03em.
 */
/** Katta raqam + ostida qisqa yorliq (S o'lcham uchun) */
export function BigNumber({ value, label, tone, className }: { value: ReactNode; label: string; tone?: string; className?: string }) {
  return (
    <div className={cn("min-w-0", className)}>
      <div className={cn("truncate text-[32px] leading-none font-semibold tracking-[-0.03em] tabular-nums", tone)}>{value}</div>
      <div className="mt-1 truncate text-[11px] text-muted">{label}</div>
    </div>
  );
}

export function WidgetEmpty({ text }: { text: string }) {
  return <div className="flex h-full items-center justify-center px-2 text-center text-[12px] text-muted">{text}</div>;
}
